'use client';

import { useState, useTransition } from 'react';
import { useRouter } from 'next/navigation';
import { createQaComment } from '#/actions/server/qa';
import { Button } from '#/components/ui/button';
import { Loader2, MessageSquarePlus, LogIn } from 'lucide-react';

interface QaCommentFormProps {
  questionId?: string;
  answerId?: string;
  parentId?: string;
  userId: string | null;
  placeholder?: string;
  onSuccess?: () => void;
}

export default function QaCommentForm({
  questionId,
  answerId,
  parentId,
  userId,
  placeholder = '댓글을 작성하세요...',
  onSuccess
}: QaCommentFormProps) {
  const router = useRouter();
  const [isPending, startTransition] = useTransition();

  const [isExpanded, setIsExpanded] = useState(false);
  const [text, setText] = useState('');
  const [error, setError] = useState('');

  // 비로그인 시 → 로그인 유도 버튼
  if (!userId) {
    return (
      <button
        onClick={() =>
          router.push(
            '/login?callbackUrl=' + encodeURIComponent(window.location.pathname)
          )
        }
        className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-[#104573] transition-colors py-1"
      >
        <LogIn className="w-4 h-4" />
        로그인 후 댓글 작성
      </button>
    );
  }

  const handleSubmit = () => {
    if (!text.trim()) return;

    setError('');
    startTransition(async () => {
      try {
        await createQaComment({
          text: text.trim(),
          questionId,
          answerId,
          parentId
        });
        setText('');
        setIsExpanded(false);
        onSuccess?.();
        router.refresh();
      } catch (err: any) {
        setError(err.message || '댓글 등록에 실패했습니다.');
      }
    });
  };

  if (!isExpanded) {
    return (
      <button
        onClick={() => setIsExpanded(true)}
        className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-[#104573] transition-colors py-1"
      >
        <MessageSquarePlus className="w-4 h-4" />
        댓글 작성
      </button>
    );
  }

  return (
    <div className="mt-2 space-y-2">
      <textarea
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          if (error) setError('');
        }}
        placeholder={placeholder}
        rows={2}
        className="w-full border border-[#104573]/20 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#104573] focus:border-transparent resize-y"
        autoFocus
      />

      {error && <p className="text-xs text-red-600">{error}</p>}

      <div className="flex items-center gap-2 justify-end">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            setIsExpanded(false);
            setText('');
            setError('');
          }}
        >
          취소
        </Button>
        <Button
          size="sm"
          onClick={handleSubmit}
          disabled={isPending || !text.trim()}
        >
          {isPending ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : (
            '등록'
          )}
        </Button>
      </div>
    </div>
  );
}
